/** Zero-dependency positioning helpers shared by Tooltip / Popover / Menu / Dialog.
 *
 *  We deliberately do NOT use @floating-ui/react. This util covers:
 *    - 4 base placements + 4 side-aligned (top, top-start, top-end, …)
 *    - auto-flip on opposite side (perpendicular axis) when not enough room
 *    - auto-flip on opposite alignment (parallel axis) when start/end overflows
 *    - viewport-edge clamp so the panel never clips
 *    - arrow offset that tracks the anchor center even after clamp (so the
 *      caret stays pointed at the trigger when the panel is shifted)
 *    - availableHeight / availableWidth on the chosen side (so consumers can
 *      cap content height to fit)
 *    - optional matchAnchorWidth — return panel width = anchor width
 *
 *  All math is in viewport coordinates. Callers portal into document.body and
 *  apply `position: fixed; top/left` from the returned PlacedRect. */

import type { CSSProperties } from 'react';

export type Side = 'top' | 'bottom' | 'left' | 'right';
export type Align = 'start' | 'center' | 'end';
export type Placement = Side | `${Side}-${Align}`;

export interface PlacedRect {
  readonly top: number;
  readonly left: number;
  readonly side: Side;
  readonly align: Align;
  /** Origin for scale/translate entry animation — matches the anchor edge. */
  readonly transformOrigin: string;
  /** Arrow offset from the panel's start edge, in panel-local px. For
   *  top/bottom placement this is the X offset; for left/right it's Y.
   *  Already clamped so the arrow never sits in the panel's rounded corner. */
  readonly arrowOffset: number;
  /** Viewport height available to the panel on the chosen side, after gap +
   *  padding. Consumers cap their content `max-height` to this so long
   *  Menus/Comboboxes shrink instead of overflowing the viewport. */
  readonly availableHeight: number;
  /** Viewport width available on the chosen side. Mirrors availableHeight. */
  readonly availableWidth: number;
  /** When `matchAnchorWidth: true` was passed, the anchor width that the
   *  panel should use. Otherwise undefined. */
  readonly width?: number;
}

export interface ComputeOptions {
  readonly placement: Placement;
  /** Gap between anchor and panel, px. */
  readonly gap: number;
  /** Minimum distance from viewport edges, px. */
  readonly padding: number;
  /** If true, flip to the opposite side when overflow would occur. */
  readonly flip: boolean;
  /** Width of the arrow (used to keep it out of the rounded corner). Default 12. */
  readonly arrowSize?: number;
}

export function parsePlacement(p: Placement): { side: Side; align: Align } {
  if (p === 'top' || p === 'bottom' || p === 'left' || p === 'right') {
    return { side: p, align: 'center' };
  }
  const [side, align] = p.split('-') as [Side, Align];
  return { side, align };
}

function oppositeSide(s: Side): Side {
  if (s === 'top') return 'bottom';
  if (s === 'bottom') return 'top';
  if (s === 'left') return 'right';
  return 'left';
}

function oppositeAlign(a: Align): Align {
  if (a === 'start') return 'end';
  if (a === 'end') return 'start';
  return 'center';
}

function transformOriginFor(side: Side, align: Align): string {
  // Origin sits on the edge that faces the anchor — so a pop-in scale grows
  // away from the anchor rather than from an arbitrary corner.
  if (side === 'top') return `${alignToPct(align)} 100%`;
  if (side === 'bottom') return `${alignToPct(align)} 0%`;
  if (side === 'left') return `100% ${alignToPct(align)}`;
  return `0% ${alignToPct(align)}`;
}

function alignToPct(a: Align): string {
  return a === 'start' ? '0%' : a === 'end' ? '100%' : '50%';
}

interface Size {
  readonly width: number;
  readonly height: number;
}
interface Rect extends Size {
  readonly top: number;
  readonly left: number;
}

function placeOnSide(
  anchor: Rect,
  panel: Size,
  side: Side,
  align: Align,
  gap: number,
): { top: number; left: number } {
  if (side === 'top') {
    return { top: anchor.top - panel.height - gap, left: alignX(anchor, panel, align) };
  }
  if (side === 'bottom') {
    return { top: anchor.top + anchor.height + gap, left: alignX(anchor, panel, align) };
  }
  if (side === 'left') {
    return { top: alignY(anchor, panel, align), left: anchor.left - panel.width - gap };
  }
  return { top: alignY(anchor, panel, align), left: anchor.left + anchor.width + gap };
}

function alignX(anchor: Rect, panel: Size, align: Align): number {
  if (align === 'start') return anchor.left;
  if (align === 'end') return anchor.left + anchor.width - panel.width;
  return anchor.left + anchor.width / 2 - panel.width / 2;
}

function alignY(anchor: Rect, panel: Size, align: Align): number {
  if (align === 'start') return anchor.top;
  if (align === 'end') return anchor.top + anchor.height - panel.height;
  return anchor.top + anchor.height / 2 - panel.height / 2;
}

/** Dimension on which `side` is measured — top/bottom care about height,
 *  left/right care about width. */
function needOf(side: Side, panel: Size): number {
  return side === 'top' || side === 'bottom' ? panel.height : panel.width;
}

/** Whether the parallel-axis alignment leaves the panel inside the viewport
 *  on that axis. Used to decide whether to flip start↔end. */
function alignFits(
  side: Side,
  align: Align,
  anchor: Rect,
  panel: Size,
  padding: number,
  vw: number,
  vh: number,
): boolean {
  if (side === 'top' || side === 'bottom') {
    const left = alignX(anchor, panel, align);
    return left >= padding && left + panel.width <= vw - padding;
  }
  const top = alignY(anchor, panel, align);
  return top >= padding && top + panel.height <= vh - padding;
}

/** Available space for the panel on `side`, after gap + padding. */
function availableOn(
  side: Side,
  anchor: Rect,
  gap: number,
  padding: number,
  vw: number,
  vh: number,
): number {
  if (side === 'top') return Math.max(0, anchor.top - gap - padding);
  if (side === 'bottom') return Math.max(0, vh - (anchor.top + anchor.height) - gap - padding);
  if (side === 'left') return Math.max(0, anchor.left - gap - padding);
  return Math.max(0, vw - (anchor.left + anchor.width) - gap - padding);
}

/** Compute the placed rect for a panel anchored to `anchor`. */
export function computePlacement(anchor: Rect, panel: Size, options: ComputeOptions): PlacedRect {
  const { side: wantedSide, align: wantedAlign } = parsePlacement(options.placement);
  const arrowSize = options.arrowSize ?? 12;
  const vw = typeof window !== 'undefined' ? window.innerWidth : Number.POSITIVE_INFINITY;
  const vh = typeof window !== 'undefined' ? window.innerHeight : Number.POSITIVE_INFINITY;

  // 1) Perpendicular flip — pick the side whose available space best
  //    accommodates the panel.
  //
  //    Old logic flipped only when the wanted side could not hold the
  //    natural panel AND the opposite side could. That left a hole: when
  //    NEITHER side fully fit, the panel stayed on the wanted side and got
  //    clamped to the viewport, even if the opposite side had dramatically
  //    more room (think: tall context menu opened near the bottom edge of
  //    a short viewport — the menu shrunk via CSS to the small bottom
  //    space, then ResizeObserver re-ran and jumped to the new position).
  //
  //    New logic: flip when the natural panel doesn't fit on the wanted
  //    side AND the opposite side has STRICTLY more room. Equal room ties
  //    stay on the wanted side, preserving the caller's intent.
  const wantedRoom = availableOn(wantedSide, anchor, options.gap, options.padding, vw, vh);
  const oppositeRoom = availableOn(
    oppositeSide(wantedSide),
    anchor,
    options.gap,
    options.padding,
    vw,
    vh,
  );
  const wantedNeed = needOf(wantedSide, panel);

  let side: Side = wantedSide;
  if (options.flip && wantedNeed > wantedRoom && oppositeRoom > wantedRoom) {
    side = oppositeSide(wantedSide);
  }

  // 2) Effective panel size — what the panel will actually render at after
  //    a consumer caps `max-height`/`max-width` to `availableHeight` /
  //    `availableWidth` (both Menu and Combobox do this via
  //    `--vq-menu-max-h`). Using the capped size for placement keeps the
  //    cursor anchored even when content overflows: without this, the
  //    first measurement uses the un-capped natural size and produces a
  //    position that *jumps* once CSS shrinks the panel and the
  //    ResizeObserver-driven `update()` recomputes against the smaller
  //    panel.
  const sideRoom = side === wantedSide ? wantedRoom : oppositeRoom;
  const effHeight =
    side === 'top' || side === 'bottom'
      ? Math.min(panel.height, sideRoom)
      : Math.min(panel.height, Math.max(0, vh - 2 * options.padding));
  const effWidth =
    side === 'left' || side === 'right'
      ? Math.min(panel.width, sideRoom)
      : Math.min(panel.width, Math.max(0, vw - 2 * options.padding));
  const effPanel: Size = { width: effWidth, height: effHeight };

  // 3) Parallel-axis alignment flip. Computed against the EFFECTIVE panel
  //    so a wide menu that the consumer will visually cap doesn't trick
  //    `alignFits` into flipping when the rendered panel would have fit.
  let align: Align = wantedAlign;
  if (options.flip && wantedAlign !== 'center') {
    const fits = alignFits(side, wantedAlign, anchor, effPanel, options.padding, vw, vh);
    if (!fits) {
      const flipped = oppositeAlign(wantedAlign);
      if (alignFits(side, flipped, anchor, effPanel, options.padding, vw, vh)) {
        align = flipped;
      }
    }
  }

  const base = placeOnSide(anchor, effPanel, side, align, options.gap);
  const left = clamp(
    base.left,
    options.padding,
    Math.max(options.padding, vw - effPanel.width - options.padding),
  );
  const top = clamp(
    base.top,
    options.padding,
    Math.max(options.padding, vh - effPanel.height - options.padding),
  );

  // 4) Arrow tracks the anchor center after clamp shifts the panel away
  //    from the trigger. Computed against effective size so the arrow's
  //    in-panel offset is always valid relative to the rendered width.
  let arrowOffset: number;
  if (side === 'top' || side === 'bottom') {
    const anchorCenterX = anchor.left + anchor.width / 2;
    arrowOffset = clamp(
      anchorCenterX - left,
      arrowSize,
      Math.max(arrowSize, effPanel.width - arrowSize),
    );
  } else {
    const anchorCenterY = anchor.top + anchor.height / 2;
    arrowOffset = clamp(
      anchorCenterY - top,
      arrowSize,
      Math.max(arrowSize, effPanel.height - arrowSize),
    );
  }

  // 5) Available space on chosen side — consumers cap their content
  //    height/width to this so long Menus shrink instead of overflowing.
  const availableHeight =
    side === 'top' || side === 'bottom'
      ? availableOn(side, anchor, options.gap, options.padding, vw, vh)
      : Math.max(0, vh - 2 * options.padding);
  const availableWidth =
    side === 'left' || side === 'right'
      ? availableOn(side, anchor, options.gap, options.padding, vw, vh)
      : Math.max(0, vw - 2 * options.padding);

  return {
    top,
    left,
    side,
    align,
    transformOrigin: transformOriginFor(side, align),
    arrowOffset,
    availableHeight,
    availableWidth,
  };
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

export function ensurePortalRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let root = document.getElementById('vq-portal-root');
  if (root === null) {
    root = document.createElement('div');
    root.id = 'vq-portal-root';
    root.className = 'vq-portal-root';
    document.body.appendChild(root);
  }
  return root;
}

export function fixedStyle(rect: PlacedRect): CSSProperties {
  return {
    position: 'fixed',
    top: `${String(rect.top)}px`,
    left: `${String(rect.left)}px`,
    transformOrigin: rect.transformOrigin,
  };
}

/** Style for the arrow element. The caller positions the arrow with `left`
 *  for top/bottom placement, or `top` for left/right. The cross-axis offset
 *  (e.g. how far the arrow sits outside the panel) is left to CSS so themes
 *  can vary the visual treatment. */
export function arrowStyle(rect: PlacedRect): CSSProperties {
  if (rect.side === 'top' || rect.side === 'bottom') {
    return { left: `${String(rect.arrowOffset)}px` };
  }
  return { top: `${String(rect.arrowOffset)}px` };
}
